import { useState, useEffect, useRef, useCallback } from "react";
import { fetchApi } from "@/services/apiService";
import { useToast } from "@/components/base/Toast";

export interface CallModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactName: string;
  phone: string;
  leadId?: string | number | null;
  agentName?: string;
  onCallLogged?: (callRecord: any) => void;
}

type CallPhase = "connecting" | "connected" | "ended";

export default function CallModal({
  isOpen,
  onClose,
  contactName,
  phone,
  leadId,
  agentName,
  onCallLogged,
}: CallModalProps) {
  const { showToast } = useToast();

  const [phase, setPhase] = useState<CallPhase>("connecting");
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [outcome, setOutcome] = useState("Connected");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopRingSoundRef = useRef<(() => void) | null>(null);

  // Clean and format phone
  const cleanPhone = (phone || "").replace(/[^\d+]/g, "");
  const dialPhone = cleanPhone.startsWith("+")
    ? cleanPhone
    : cleanPhone.length === 10
    ? `+91${cleanPhone}`
    : cleanPhone;

  // Web Audio native phone ring tone synthesizer (Pure browser native, no external assets)
  const startRingingSound = useCallback(() => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      let active = true;
      let loopTimeout: any = null;

      const ringPulse = () => {
        if (!active || ctx.state === "closed") return;
        try {
          const osc1 = ctx.createOscillator();
          const osc2 = ctx.createOscillator();
          const gain = ctx.createGain();

          osc1.type = "sine";
          osc2.type = "sine";
          osc1.frequency.setValueAtTime(440, ctx.currentTime);
          osc2.frequency.setValueAtTime(480, ctx.currentTime);

          gain.gain.setValueAtTime(0.06, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.4);

          osc1.connect(gain);
          osc2.connect(gain);
          gain.connect(ctx.destination);

          osc1.start(ctx.currentTime);
          osc2.start(ctx.currentTime);
          osc1.stop(ctx.currentTime + 1.4);
          osc2.stop(ctx.currentTime + 1.4);

          loopTimeout = setTimeout(ringPulse, 3200);
        } catch {
          // ignore
        }
      };

      ringPulse();

      stopRingSoundRef.current = () => {
        active = false;
        if (loopTimeout) clearTimeout(loopTimeout);
        try {
          ctx.close();
        } catch {}
      };
    } catch {
      // Audio context might be restricted before user gesture
    }
  }, []);

  const stopRingingSound = useCallback(() => {
    if (stopRingSoundRef.current) {
      stopRingSoundRef.current();
      stopRingSoundRef.current = null;
    }
  }, []);

  // When modal opens, initialize in-CRM direct call
  useEffect(() => {
    if (isOpen) {
      setPhase("connecting");
      setDuration(0);
      setIsMuted(false);
      setIsOnHold(false);
      setOutcome("Connected");
      setNotes("");

      // Start in-browser ringing sound
      startRingingSound();
    } else {
      stopRingingSound();
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      stopRingingSound();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isOpen, startRingingSound, stopRingingSound]);

  // Duration stopwatch when in connected phase
  useEffect(() => {
    if (phase === "connected") {
      stopRingingSound();
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [phase, stopRingingSound]);

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const handleConnectNow = () => {
    stopRingingSound();
    setPhase("connected");
  };

  const handleEndCall = () => {
    stopRingingSound();
    if (timerRef.current) clearInterval(timerRef.current);
    setPhase("ended");
  };

  const handleSaveCall = async () => {
    setSaving(true);
    const formattedDur = formatDuration(duration);
    const currentAgent = agentName || localStorage.getItem("user_name") || "Agent";

    try {
      const res = await fetchApi<any>("/calls", {
        method: "POST",
        body: JSON.stringify({
          contact_name: contactName || "Contact",
          phone: dialPhone || phone,
          direction: "outbound",
          duration: formattedDur,
          status: outcome === "Busy" ? "Busy" : outcome === "No Answer" ? "Missed" : "Completed",
          outcome: outcome || "Connected",
          notes: notes.trim() || undefined,
          lead_id: leadId || null,
          agent: currentAgent,
        }),
      });

      showToast(`Call record with ${contactName} saved to CRM!`, "success");
      if (onCallLogged) {
        onCallLogged(res?.data || res);
      }
      onClose();
    } catch (err: any) {
      console.error("Save call error:", err);
      showToast(err?.message || "Failed to save call record", "error");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const initials = (contactName || "Contact")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/55 backdrop-blur-xs animate-fade-in">
      <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 flex flex-col animate-scale-up">
        {/* Top Header Glow Bar */}
        <div
          className={`h-2.5 w-full transition-colors duration-300 ${
            phase === "connecting"
              ? "bg-amber-500 animate-pulse"
              : phase === "connected"
              ? "bg-emerald-500"
              : "bg-indigo-600"
          }`}
        />

        {/* Close button */}
        <button
          onClick={() => {
            stopRingingSound();
            onClose();
          }}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-colors cursor-pointer z-20"
        >
          <i className="ri-close-line text-lg" />
        </button>

        <div className="p-7 text-center flex flex-col items-center">
          {/* Avatar with Calling Waves */}
          <div className="relative my-3 flex items-center justify-center">
            {phase === "connecting" && (
              <>
                <span className="absolute w-24 h-24 rounded-full bg-emerald-500/20 animate-ping" />
                <span className="absolute w-32 h-32 rounded-full bg-emerald-500/10 animate-pulse" />
              </>
            )}
            {phase === "connected" && (
              <span className="absolute w-24 h-24 rounded-full bg-emerald-500/25 animate-pulse" />
            )}

            <div
              className={`w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-xl transition-all duration-300 z-10 ${
                phase === "connecting"
                  ? "bg-emerald-600 ring-4 ring-emerald-200"
                  : phase === "connected"
                  ? "bg-emerald-600 ring-4 ring-emerald-300"
                  : "bg-indigo-600"
              }`}
            >
              {initials}
            </div>
          </div>

          {/* Contact Details */}
          <h3 className="text-xl font-bold text-gray-900 tracking-tight mt-1">{contactName}</h3>
          <div className="flex items-center gap-1.5 mt-1 text-xs text-gray-500 font-medium">
            <i className="ri-phone-fill text-emerald-600" />
            <span className="text-gray-700 font-semibold">{dialPhone || "No phone"}</span>
          </div>

          {/* ── PHASE 1: RINGING / CONNECTING ── */}
          {phase === "connecting" && (
            <div className="mt-6 w-full flex flex-col items-center">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold shadow-xs">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping" />
                Ringing in CRM...
              </div>

              <p className="text-xs text-gray-500 mt-2 text-center">
                Baat shuru hone par <strong>"Start Call / Connected"</strong> par click karein.
              </p>

              <div className="mt-6 flex items-center justify-center gap-3 w-full">
                <button
                  onClick={handleConnectNow}
                  className="flex-1 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 shadow-md active:scale-98"
                >
                  <i className="ri-phone-fill text-base" /> Start Call / Connected
                </button>
                <button
                  onClick={handleEndCall}
                  className="py-3 px-5 rounded-2xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
                >
                  <i className="ri-phone-fill rotate-[135deg] text-base" /> Decline
                </button>
              </div>
            </div>
          )}

          {/* ── PHASE 2: IN CALL (CONNECTED) ── */}
          {phase === "connected" && (
            <div className="mt-5 w-full flex flex-col items-center">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold shadow-xs">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                Call Connected
              </div>

              {/* Stopwatch timer */}
              <p className="font-mono text-4xl font-extrabold text-gray-900 tracking-wider my-3.5">
                {formatDuration(duration)}
              </p>

              {/* In-Call Controls */}
              <div className="flex items-center justify-center gap-4 mt-2 w-full">
                <button
                  onClick={() => setIsMuted((prev) => !prev)}
                  className={`w-12 h-12 rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer shadow-xs ${
                    isMuted ? "bg-amber-100 text-amber-700 ring-2 ring-amber-400" : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  }`}
                  title={isMuted ? "Unmute Mic" : "Mute Mic"}
                >
                  <i className={`${isMuted ? "ri-mic-off-line" : "ri-mic-line"} text-xl`} />
                  <span className="text-[9px] font-bold mt-0.5">{isMuted ? "Muted" : "Mute"}</span>
                </button>

                <button
                  onClick={() => setIsOnHold((prev) => !prev)}
                  className={`w-12 h-12 rounded-2xl flex flex-col items-center justify-center transition-all cursor-pointer shadow-xs ${
                    isOnHold ? "bg-amber-100 text-amber-700 ring-2 ring-amber-400" : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                  }`}
                  title={isOnHold ? "Resume Call" : "Hold Call"}
                >
                  <i className={`${isOnHold ? "ri-play-line" : "ri-pause-line"} text-xl`} />
                  <span className="text-[9px] font-bold mt-0.5">{isOnHold ? "Holding" : "Hold"}</span>
                </button>

                <button
                  onClick={handleEndCall}
                  className="w-14 h-14 rounded-2xl bg-red-600 hover:bg-red-700 text-white flex flex-col items-center justify-center shadow-lg transition-transform active:scale-95 cursor-pointer"
                  title="End Call"
                >
                  <i className="ri-phone-fill text-2xl rotate-[135deg]" />
                  <span className="text-[9px] font-bold mt-0.5">End</span>
                </button>
              </div>
            </div>
          )}

          {/* ── PHASE: ENDED (LOG CALL SUMMARY) ── */}
          {phase === "ended" && (
            <div className="mt-4 w-full text-left">
              <div className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border border-gray-200 mb-3">
                <span className="text-xs text-gray-600 font-medium">Total Duration</span>
                <span className="text-xs font-mono font-bold text-gray-900">{formatDuration(duration)}</span>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Call Outcome</label>
                  <select
                    value={outcome}
                    onChange={(e) => setOutcome(e.target.value)}
                    className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-300 bg-white text-gray-900 focus:outline-none focus:border-indigo-500 cursor-pointer"
                  >
                    <option value="Connected">Connected - Discussion Complete</option>
                    <option value="Interested">Interested / Hot Lead</option>
                    <option value="Follow Up Required">Follow Up Required</option>
                    <option value="Busy">Line Busy</option>
                    <option value="No Answer">No Answer / Rang Once</option>
                    <option value="Wrong Number">Wrong Number</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Call Notes</label>
                  <textarea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Discussed pricing quotation, will share brochure on WhatsApp..."
                    className="w-full px-3 py-2 text-xs rounded-lg border border-gray-300 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={onClose}
                    className="flex-1 py-2 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  >
                    Discard
                  </button>
                  <button
                    onClick={handleSaveCall}
                    disabled={saving}
                    className="flex-2 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm cursor-pointer disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" /> Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line" /> Save Call Log
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
